import { memo } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Divider,
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'

function CsvDownloadDialog({
  open,
  appliedSearchQuery,
  totalCount,
  downloading,
  error,
  csvDownloadAll,
  csvLimit,
  selectedColumns,
  availableColumns,
  downloadAllLabel = 'Download All',
  downloadAllDescription = 'Download all items matching your current search (no limit)',
  limitLabel = 'Number of Items to Download',
  resultsUnit = 'item(s)',
  onClose,
  onDownload,
  onClearError,
  onCsvDownloadAllChange,
  onCsvLimitChange,
  onToggleAllColumns,
  onToggleColumn,
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, bgcolor: '#f8f9fb' }}>Download CSV Options</DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <Alert severity="error" onClose={onClearError} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={csvDownloadAll}
                onChange={(e) => onCsvDownloadAllChange(e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography sx={{ fontWeight: 600 }}>{downloadAllLabel}</Typography>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  {downloadAllDescription}
                </Typography>
              </Box>
            }
          />

          {!csvDownloadAll && (
            <TextField
              label={limitLabel}
              type="number"
              value={csvLimit}
              onChange={(e) => onCsvLimitChange(e.target.value)}
              inputProps={{ min: 1 }}
              helperText={`Current search results: ${totalCount} ${resultsUnit}`}
              fullWidth
              size="small"
            />
          )}

          <Divider />

          <Box>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
            >
              <Typography sx={{ fontWeight: 600 }}>Select Columns</Typography>
              <Button size="small" onClick={onToggleAllColumns} sx={{ minWidth: 'auto', px: 2 }}>
                {selectedColumns.length === availableColumns.length ? 'Deselect All' : 'Select All'}
              </Button>
            </Box>

            <FormGroup
              sx={{
                maxHeight: 300,
                overflowY: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                p: 2,
              }}
            >
              {availableColumns.map((column) => (
                <FormControlLabel
                  key={column.id}
                  control={
                    <Checkbox
                      checked={selectedColumns.includes(column.id)}
                      onChange={() => onToggleColumn(column.id)}
                      size="small"
                    />
                  }
                  label={column.label}
                />
              ))}
            </FormGroup>

            <Typography variant="caption" sx={{ color: '#666', mt: 1, display: 'block' }}>
              {selectedColumns.length} of {availableColumns.length} columns selected
            </Typography>
          </Box>

          {appliedSearchQuery?.trim() && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Only items matching your search query &quot;{appliedSearchQuery}&quot; will be
              downloaded
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, bgcolor: '#f8f9fb' }}>
        <Button onClick={onClose} disabled={downloading} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={onDownload}
          variant="contained"
          disabled={downloading || selectedColumns.length === 0}
          sx={{ bgcolor: '#e90000', '&:hover': { bgcolor: '#1b5e20' } }}
          startIcon={downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
        >
          {downloading ? 'Downloading...' : 'Download CSV'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default memo(CsvDownloadDialog)
